/**
 * Turn — the atomic unit of conversation.
 *
 * FVC 172-spine. New entity kind, additive on top of the existing
 * EntityKind taxonomy (see src/core/adapters/filesystem/types.ts).
 *
 * One Turn represents one user input + everything VC did in response
 * (model calls, tool calls, agent spawns — captured as Spans under
 * this Turn). Conversational state is composed of Turns.
 *
 * Pattern parity with Conversation.ts / Thread.ts:
 *   - `id` (UUID/ULID) + `fcid: fcid://turn/<id>` canonical reference
 *   - `create<Kind>(input): <Kind>` factory with sensible defaults
 *   - `is<Kind>(value): value is <Kind>` runtime type guard
 *   - `createdAt` / `updatedAt` as ISO 8601 strings
 *   - `version: 1` field for forward-only schema evolution (FvW §43)
 *
 * Linkage:
 *   - conversationId → existing `conversation` kind (NOT modified)
 *   - agentId → `agent` kind (sibling, defined in Agent.ts)
 *   - parentTurnId → another `turn` (for child turns spawned in another agent)
 *   - spawnedAgentId → `agent` kind (the agent this turn created)
 *
 * REDACTION: turn.input and turn.output store the user message and the
 * response text. Turns are user-facing artifacts; their input/output is
 * expected to be plain user-typed text. For spans (subtelemetry), see
 * Span.ts — spans default to NO input/output storage for safety.
 */

export type TurnInitiatedBy = 'user' | 'agent';
export type TurnStatus = 'complete' | 'failed' | 'interrupted';

export interface Turn {
  id: string;
  fcid: string;
  project: string;
  title: string;

  /** Links to the parent conversation (existing `conversation` kind) */
  conversationId: string;
  /** Which agent owns this turn */
  agentId: string;
  /** If this turn spawned a child turn in another agent, point at it */
  parentTurnId: string | null;
  /** If this turn spawned an agent, point at the new agent */
  spawnedAgentId: string | null;

  initiatedBy: TurnInitiatedBy;
  /** Local sequence number within its conversation (1-indexed) */
  sequence: number;

  startedAt: string;
  endedAt: string | null;
  duration: number | null; // milliseconds

  /** User message or triggering action */
  input: string;
  /** VC's response */
  output: string;

  status: TurnStatus;
  /** Cached roll-up sourced from spans (cheap to display without recompute) */
  costSummary: { tokens?: number; cost?: number; spans?: number } | null;

  /** Schema version for forward-only evolution (FvW §43) */
  version: 1;
  createdAt: string;
  updatedAt: string;
}

export function createTurn(input: {
  conversationId: string;
  agentId: string;
  input: string;
  project: string;
  /** Optional title (auto-derived from input if absent) */
  title?: string;
  parentTurnId?: string | null;
  spawnedAgentId?: string | null;
  initiatedBy?: TurnInitiatedBy;
  /** Local sequence number within the conversation; default 0 */
  sequence?: number;
  startedAt?: string;
  output?: string;
  endedAt?: string | null;
  status?: TurnStatus;
  costSummary?: Turn['costSummary'];
  id?: string;
}): Turn {
  const now = new Date().toISOString();
  const id = input.id ?? generateTurnId();
  return {
    id,
    fcid: `fcid://turn/${id}`,
    project: input.project,
    title: input.title ?? deriveTitle(input.input),
    conversationId: input.conversationId,
    agentId: input.agentId,
    parentTurnId: input.parentTurnId ?? null,
    spawnedAgentId: input.spawnedAgentId ?? null,
    initiatedBy: input.initiatedBy ?? 'user',
    sequence: input.sequence ?? 0,
    startedAt: input.startedAt ?? now,
    endedAt: input.endedAt ?? null,
    duration: null,
    input: input.input,
    output: input.output ?? '',
    status: input.status ?? 'complete',
    costSummary: input.costSummary ?? null,
    version: 1,
    createdAt: now,
    updatedAt: now,
  };
}

export function generateTurnId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `turn-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function isTurn(value: unknown): value is Turn {
  if (!value || typeof value !== 'object') return false;
  const t = value as Record<string, unknown>;
  return (
    typeof t.id === 'string' &&
    typeof t.conversationId === 'string' &&
    typeof t.agentId === 'string' &&
    typeof t.project === 'string' &&
    typeof t.fcid === 'string' &&
    t.fcid.startsWith('fcid://turn/') &&
    typeof t.input === 'string' &&
    typeof t.output === 'string' &&
    typeof t.sequence === 'number' &&
    (t.initiatedBy === 'user' || t.initiatedBy === 'agent') &&
    (t.status === 'complete' || t.status === 'failed' || t.status === 'interrupted') &&
    t.version === 1
  );
}

/**
 * Derive a short title from the input. Used when no title is provided.
 * Trims whitespace, takes first line, caps at 80 chars.
 */
function deriveTitle(input: string): string {
  const firstLine = (input ?? '').split('\n')[0].trim();
  if (!firstLine) return '(empty turn)';
  if (firstLine.length <= 80) return firstLine;
  return firstLine.slice(0, 77) + '...';
}
